import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  CheckCircle2, Circle, Plus, ClipboardList, Clock, Pill, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  medication: { icon: Pill, color: 'text-rose-400', bg: 'bg-rose-400/10', label: 'Medication' },
  task: { icon: ClipboardList, color: 'text-[#00A6A0]', bg: 'bg-[#00A6A0]/10', label: 'Task' },
  check: { icon: CheckCircle2, color: 'text-amber-400', bg: 'bg-amber-400/10', label: 'Check' },
};

export default function CaregiverTasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [showAdd, setShowAdd] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', description: '', time: '' });

  const fetchTasks = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/caregiver-management/tasks?date=${date}`, { withCredentials: true });
      setTasks(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTasks(); }, [date]);

  const handleComplete = async (taskId) => {
    if (taskId.startsWith('plan_')) return;
    try {
      await axios.put(`${API_URL}/api/caregiver-management/tasks/${taskId}/complete`, {}, { withCredentials: true });
      toast.success('Task completed');
      fetchTasks();
    } catch (err) {
      toast.error('Failed to complete task');
    }
  };

  const handleAdd = async () => {
    if (!newTask.title.trim()) return;
    try {
      await axios.post(`${API_URL}/api/caregiver-management/tasks`, {
        ...newTask, date
      }, { withCredentials: true });
      toast.success('Task added');
      setNewTask({ title: '', description: '', time: '' });
      setShowAdd(false);
      fetchTasks();
    } catch (err) {
      toast.error('Failed to add task');
    }
  };

  const completed = tasks.filter(t => t.completed);
  const pending = tasks.filter(t => !t.completed);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="caregiver-tasks-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground">My Tasks</h1>
            <p className="text-sm text-muted-foreground mt-1">Daily tasks from shifts and care plans</p>
          </div>
          <div className="flex items-center gap-3">
            <Input
              type="date" value={date}
              onChange={e => setDate(e.target.value)}
              className="w-auto bg-muted border-border text-foreground"
              data-testid="tasks-date-picker"
            />
            <Button onClick={() => setShowAdd(!showAdd)} className="btn-primary gap-2" data-testid="add-task-btn">
              <Plus className="w-4 h-4" /> Add Task
            </Button>
          </div>
        </div>

        {showAdd && (
          <Card className="glass-card border-border">
            <CardContent className="p-4 space-y-3">
              <Input placeholder="Task title" value={newTask.title}
                onChange={e => setNewTask({ ...newTask, title: e.target.value })}
                className="bg-muted border-border" data-testid="new-task-title" />
              <div className="flex gap-3">
                <Input placeholder="Description (optional)" value={newTask.description}
                  onChange={e => setNewTask({ ...newTask, description: e.target.value })}
                  className="bg-muted border-border flex-1" data-testid="new-task-desc" />
                <Input type="time" value={newTask.time}
                  onChange={e => setNewTask({ ...newTask, time: e.target.value })}
                  className="bg-muted border-border w-36" data-testid="new-task-time" />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" onClick={() => setShowAdd(false)}>Cancel</Button>
                <Button onClick={handleAdd} className="btn-primary" data-testid="save-task-btn">Save</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="glass-card border-border">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-foreground">{tasks.length}</p>
              <p className="text-xs text-muted-foreground">Total</p>
            </CardContent>
          </Card>
          <Card className="glass-card border-border">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-400">{pending.length}</p>
              <p className="text-xs text-muted-foreground">Pending</p>
            </CardContent>
          </Card>
          <Card className="glass-card border-border">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-[#A8E6CF]">{completed.length}</p>
              <p className="text-xs text-muted-foreground">Done</p>
            </CardContent>
          </Card>
        </div>

        {/* Pending Tasks */}
        <Card className="glass-card border-border">
          <CardHeader className="border-b border-border">
            <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-400" /> Pending ({pending.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {pending.length === 0 ? (
              <div className="p-8 text-center">
                <CheckCircle2 className="w-10 h-10 text-[#A8E6CF] mx-auto mb-3" />
                <p className="text-muted-foreground">All tasks completed!</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {pending.map(task => {
                  const cfg = TYPE_CONFIG[task.type] || TYPE_CONFIG.task;
                  const Icon = cfg.icon;
                  return (
                    <div key={task.id} className="p-4 flex items-center gap-4 hover:bg-muted/30 transition-colors" data-testid={`task-${task.id}`}>
                      <button onClick={() => handleComplete(task.id)} className="flex-shrink-0"
                        disabled={task.source === 'care_plan'} data-testid={`complete-task-${task.id}`}>
                        <Circle className="w-5 h-5 text-muted-foreground hover:text-[#00A6A0] transition-colors" />
                      </button>
                      <div className={`p-2 rounded-lg ${cfg.bg} flex-shrink-0`}>
                        <Icon className={`w-4 h-4 ${cfg.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-foreground font-medium text-sm">{task.title}</p>
                        {task.description && <p className="text-xs text-muted-foreground truncate">{task.description}</p>}
                        {task.client_name && <p className="text-xs text-[#00A6A0]">{task.client_name}</p>}
                      </div>
                      {task.time && (
                        <span className="text-xs text-muted-foreground font-mono flex-shrink-0">{task.time}</span>
                      )}
                      {task.source === 'care_plan' && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#00A6A0]/10 text-[#00A6A0]">Care Plan</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Completed Tasks */}
        {completed.length > 0 && (
          <Card className="glass-card border-border">
            <CardHeader className="border-b border-border">
              <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-[#A8E6CF]" /> Completed ({completed.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                {completed.map(task => (
                  <div key={task.id} className="p-4 flex items-center gap-4 opacity-60" data-testid={`task-done-${task.id}`}>
                    <CheckCircle2 className="w-5 h-5 text-[#A8E6CF] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-foreground text-sm line-through">{task.title}</p>
                    </div>
                    {task.time && <span className="text-xs text-muted-foreground font-mono">{task.time}</span>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
